/**
 * LifeThemeQuestionCarousel
 * Återanvändbar komponent som visar karusell med livstema-frågor
 * Kan användas på både Landing Page och Express Page
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { RefreshCw, ArrowRight, Loader } from 'lucide-react';
import { trackEvent } from '../utils/analytics';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const LifeThemeQuestionCarousel = ({ source = 'unknown', compact = false }) => {
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchQuestions();
  }, []);

  const fetchQuestions = async (isRefresh = false) => {
    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    setError(null);

    try {
      const response = await fetch(`${API_BASE}/api/life-theme-identifier/questions/carousel/public`);
      
      if (!response.ok) {
        throw new Error('Kunde inte hämta frågor');
      }
      
      const data = await response.json();
      setQuestions(data);
    } catch (err) {
      console.error('Error fetching questions:', err);
      setError('Kunde inte ladda frågor. Försök igen senare.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleSelectQuestion = (question) => {
    // Track question selection (public flow)
    trackEvent('Life Theme Question Select Public', {
      theme_id: question.theme_id,
      theme_name: question.theme_name,
      question_id: question.id,
      location: source,
      flow_type: 'public_carousel'
    });
    
    // Spara valt tema och fråga i sessionStorage
    sessionStorage.setItem('selectedLifeTheme', JSON.stringify({
      id: question.theme_id,
      name: question.theme_name
    }));
    
    // Navigera till frågeflödet för detta tema
    navigate(`/utforska-livstema/${question.theme_id}`);
  };

  const handleRefresh = () => {
    trackEvent('Life Theme Refresh Questions Public', {
      location: source,
      flow_type: 'public_carousel'
    });
    fetchQuestions(true);
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <Loader className="w-12 h-12 text-brand-primary animate-spin mx-auto mb-4" />
        <p className="text-secondary">Laddar frågor...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-primary p-6 rounded-2xl shadow-custom-lg border border-light text-center">
        <p className="text-error-700 mb-4">{error}</p>
        <button
          onClick={() => fetchQuestions()}
          className="px-6 py-3 bg-brand-primary text-white rounded-lg hover:bg-brand-primary-dark transition-custom"
        >
          Försök igen
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Question Cards */}
      <div className={`space-y-4 ${compact ? 'mb-6' : 'mb-8'}`}>
        {questions.map((question, index) => (
          <button
            key={question.id}
            onClick={() => handleSelectQuestion(question)}
            className="w-full bg-primary p-6 rounded-2xl border-2 border-light hover:border-brand-primary transition-all duration-200 text-left group shadow-sm hover:shadow-md"
          >
            <div className="flex items-start gap-4">
              <div className="flex-1">
                <p className={`${compact ? 'text-base' : 'text-lg'} text-primary leading-relaxed`}>
                  {question.question_text}
                </p>
              </div>

              <ArrowRight className="w-6 h-6 text-tertiary group-hover:text-brand-primary group-hover:translate-x-1 transition-all flex-shrink-0 mt-1" />
            </div>
          </button>
        ))}
      </div>

      {/* Refresh Button */}
      <div className="flex justify-center">
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="flex items-center gap-2 px-6 py-3 bg-surface-primary border border-light rounded-lg hover:bg-surface-secondary transition-custom disabled:opacity-50"
        >
          <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
          {refreshing ? 'Laddar nya frågor...' : 'Visa 5 nya påståenden'}
        </button>
      </div>
    </div>
  );
};

export default LifeThemeQuestionCarousel;
